import { useEffect, useState } from 'react';
import { useDomain } from '../../../../states/AppDomainProvider';
import { getAuthoredLessons } from '../../../../services/LessonService';
import Logger from '../../../../utils/Logger';
import { createLessonObject, mapLessonsIntoSubject, sortLessonAttributes } from '../../../../utils/LessonsMapping';
import { LOADER_TYPE } from '../../../../components/loaders/SelectableLoader';
import { searchTeacherCourseLessons } from '../../../../services/CourseService';
import { filterByKeyword } from '../../../../utils/LessonUtils';
import { useAuthoredLessonsDomain } from './stores/AuthoredLessonsDomainProvider';

export const PENDING_VALUE = 'pending';

export const FILTER_STATUS = [
  {
    label: 'My Resources',
    value: 'my_lessons',
  },
  {
    label: 'Pending Lesson',
    value: 'pending',
  },
  {
    label: 'Approved for Courses',
    value: 'approved_for_courses',
  },
];

export const FILTER_STATUS_FOR_COPY = [
  {
    label: 'My Resources',
    value: 'my_lessons',
  },
  {
    label: 'Pending Lesson',
    value: 'pending',
  },
  {
    label: 'Auto generated',
    value: 'auto-generated',
  },
];

export const getLessonsFromCourseId = async ({
  courseId,
  topicId,
  uiDomain,
  isLoadedFlag,
  setComponentLoading,
  authoredLessonDomain,
  user,
}) => {
  Logger.logWhenDebugModeIsOn('Fetching your authored activities from server');
  uiDomain.showLoader('Fetching your authored activities', LOADER_TYPE.HASH_LOADER);

  if (!isLoadedFlag) {
    setComponentLoading(true);
  }

  try {
    const result = await searchTeacherCourseLessons({
      courseId,
      topicId,
    });
    if (result.data) {
      const lessonsInGroup = [];
      const lessonsWithoutLessonType = [];
      let lessons = [];

      result.data.forEach((lesson) => {
        const lessonData = createLessonObject(lesson, user);
        const { attributes: { lessonType, topicLessonSequenceNum } } = lessonData;
        if (lessonType === null || lessonType === undefined || topicLessonSequenceNum === null || topicLessonSequenceNum === undefined) {
          lessonsWithoutLessonType.push(lessonData);
        } else {
          lessons.push(lessonData);
        }
      });

      lessons = sortLessonAttributes(lessons, 'topicLessonSequenceNum');
      lessonsInGroup.push({
        subject: 'Activities',
        lessons: [
          ...lessons,
          ...lessonsWithoutLessonType,
        ],
      });

      await authoredLessonDomain.updateAuthoredLessons(lessonsInGroup, result.included);
    } else {
      await authoredLessonDomain.updateAuthoredLessons([], []);
    }
  } catch (e) {
    Logger.logWhenDebugModeIsOn(e);
  }
  uiDomain.hideLoader();
};

export const useFetchAuthoredFromService = () => {
  const [componentLoading, setComponentLoading] = useState(false);
  const { authoredLessonsSearchCriteriaDomain } = useAuthoredLessonsDomain();
  const { authoredLessonDomain, uiDomain, userDomain } = useDomain();
  const { isLoadedFlag } = authoredLessonDomain.domainData;
  const { user } = userDomain.domainData;

  const fetchAuthoredFromService = async (status, courseId, topicId) => {
    if (status === FILTER_STATUS[2].value) {
      if (courseId) {
        await authoredLessonsSearchCriteriaDomain.updateAuthoredLessonPendingStatus(status);
        await getLessonsFromCourseId({
          courseId,
          topicId,
          uiDomain,
          isLoadedFlag,
          setComponentLoading,
          authoredLessonDomain,
          user,
        });
      } else {
        await authoredLessonsSearchCriteriaDomain.updateAuthoredLessonPendingStatus(status);
        await authoredLessonDomain.updateAuthoredLessons([], []);
      }
      return;
    }

    Logger.logWhenDebugModeIsOn('Fetching your authored activities from server');
    uiDomain.showLoader('Fetching your authored activities', LOADER_TYPE.HASH_LOADER);

    if (!isLoadedFlag) {
      setComponentLoading(true);
    }
    await authoredLessonsSearchCriteriaDomain.updateAuthoredLessonPendingStatus(status);
    const result = await getAuthoredLessons(status);
    await authoredLessonDomain.updatteLoadedFlag(true);
    Logger.logWhenDebugModeIsOn({
      data: result,
    });
    if (result.data && result.data.length > 0) {
      setComponentLoading(false);
      const subjectListWithLessons = mapLessonsIntoSubject(result, user);
      await authoredLessonDomain.updateAuthoredLessons(subjectListWithLessons, result.included);
      await uiDomain.hideLoader();
    } else {
      await uiDomain.hideLoader();
      // Redirect only once when the user login.
      // After that if the user get to this screen again,
      // the user should not be redirected to the Get Start page
      setComponentLoading(false);
    }
  };

  return {
    fetchAuthoredFromService,
    componentLoading,
  };
};

export const usePrepareData = (fetchAuthoredFromService) => {
  const { uiDomain, userDomain } = useDomain();
  const { authoredLessonsSearchCriteriaDomain } = useAuthoredLessonsDomain();
  const { selectedPendingStatus } = authoredLessonsSearchCriteriaDomain.domainData;
  const { user } = userDomain.domainData;
  const initData = async () => {
    let status = selectedPendingStatus;
    if (status === null || status === undefined) {
      if (user.userModerator) {
        status = FILTER_STATUS[1].value;
      } else if (user.userReviewer) {
        status = 'reviewer';
      } else {
        status = FILTER_STATUS[0].value;
      }
    }
    await fetchAuthoredFromService(status);
  };

  useEffect(() => {
    initData();

    return () => {
      uiDomain.hideLoader();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useGetFilteredLessons = () => {
  const { authoredLessonDomain } = useDomain();
  const { authoredLessonsSearchCriteriaDomain } = useAuthoredLessonsDomain();
  const { authoredLessons } = authoredLessonDomain.domainData;
  const { filteredText, selectedPendingStatus } = authoredLessonsSearchCriteriaDomain.domainData;
  const [filteredLessons, setFilteredLessons] = useState(authoredLessons);
  const [hasLessonFlag, setHasLesson] = useState(
    {
      hasLesson: false,
      message: '',
    },
  );

  useEffect(() => {
    const result = [];

    authoredLessons.forEach((al) => {
      const subjectRow = { ...al };
      if (filteredText && filteredText.trim().length > 0) {
        subjectRow.lessons = subjectRow.lessons.filter((l) => (
          filterByKeyword(filteredText, l)
        )) || [];
      }

      result.push(subjectRow);
    });

    const isNotEmpty = result.findIndex((s) => s.lessons.length > 0) >= 0;

    if (isNotEmpty) {
      setHasLesson({
        hasLesson: true,
        message: '',
      });
    } else if (!isNotEmpty && selectedPendingStatus === FILTER_STATUS[2].value) {
      setHasLesson({
        hasLesson: false,
        message: 'No Activities Available, please select course',
      });
    } else {
      setHasLesson({
        hasLesson: false,
        message: 'No Activities Available',
      });
    }

    setFilteredLessons(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authoredLessons, filteredText]);

  return {
    hasLessonFlag,
    filteredLessons,
    selectedPendingStatus,
  };
};
